import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Grid, Group, LoadingOverlay } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { MainLayout } from '../../layouts'
import { ContentBox, BackListButton, SaveButton, ResetButton } from '../../partials'
import { FormTypes } from '../../../types'
import { useAppDispatch, useAppSelector } from '../../../store'
import { IGroupParams, GroupType } from '../../../store/types'
import { IFormRef } from '../../../types'
import { getFormattedValidationErrors } from '../../../utils/mixins'
import Form from './Form'
import { fetchShowGroup, resetShowGroup } from '../../../store/features/groups/showGroupSlice'
import { fetchUpdateGroup, resetUpdateGroup } from '../../../store/features/groups/updateGroupSlice'

const Edit = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const formRef = useRef<IFormRef>(null)
  const dispatch = useAppDispatch()
  const [data, setData] = useState<IGroupParams | null>(null)

  const {
    isLoading: groupIsLoading,
    response: groupResponse,
    error: groupError
  } = useAppSelector(state => state.showGroup)
  const { isLoading, response, error } = useAppSelector(state => state.updateGroup)

  useEffect(() => {
    if (groupResponse === null && data === null) {
      ;(async () => {
        await dispatch(fetchShowGroup(id as string))
      })()
    }
  }, [groupResponse, data, id, dispatch])

  const getSerializedData = (data: GroupType) => {
    return {
      name: data.name
    }
  }

  useEffect(() => {
    if (groupResponse !== null) {
      setData(getSerializedData(groupResponse.data))

      if (data !== null) {
        dispatch(resetShowGroup())
      }
    }
  }, [groupResponse, data, dispatch])

  useEffect(() => {
    if (groupError !== null) {
      if (groupError.response.status === 401 && id) {
        ;(async () => {
          dispatch(resetShowGroup())
          await dispatch(fetchShowGroup(id))
        })()
      } else if (groupError.response.status === 404) {
        dispatch(resetShowGroup())
        showNotification({
          color: 'red',
          title: t('error'),
          message: t(groupError.data.message)
        })

        navigate('/groups')
      } else {
        dispatch(resetShowGroup())
        showNotification({
          color: 'red',
          title: t('error'),
          message: t('errors.unknown')
        })

        navigate('/groups')
      }
    }
  }, [groupError, id, dispatch, t, navigate])

  useEffect(() => {
    if (response !== null) {
      setData(getSerializedData(response.data))

      showNotification({
        color: 'green',
        title: t('successful'),
        message: t('updated')
      })

      dispatch(resetUpdateGroup())
    }
  }, [response, t, dispatch])

  useEffect(() => {
    if (error !== null) {
      if (error.response.status === 422) {
        formRef.current!.setErrors(getFormattedValidationErrors(error))
      } else {
        showNotification({
          color: 'red',
          title: t('unsuccessful'),
          message: error.response.data ? error.response.data.message : t('errors.unknown')
        })
      }
    }
  }, [error, t])

  useEffect(() => {
    if (response !== null) {
      dispatch(resetUpdateGroup())
      formRef.current!.reset()
    }
  }, [response, dispatch])

  const handleSubmit = async (params: IGroupParams) => {
    await dispatch(fetchUpdateGroup({ id: id as string, params }))
  }

  return (
    <MainLayout>
      <LoadingOverlay visible={groupIsLoading || isLoading || !data} />
      {data && (
        <ContentBox title={t('content_actions.create')}>
          <Form
            id="edit-group-form"
            type={FormTypes.EDIT}
            ref={formRef}
            initialValues={data}
            onSubmit={handleSubmit}
          />
        </ContentBox>
      )}
      <ContentBox>
        <Grid>
          <Grid.Col span={6}>
            <BackListButton to="/groups" />
          </Grid.Col>
          <Grid.Col span={6}>
            <Group position="right">
              <SaveButton form="edit-group-form" />
              <ResetButton form="edit-group-form" />
            </Group>
          </Grid.Col>
        </Grid>
      </ContentBox>
    </MainLayout>
  )
}

export default Edit
